import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Flex, Text } from 'core/components';
import { Field, formConsumer, Select } from 'core/form';
import { UdeFormProps } from 'app/views/boards/panels/ude/form.types';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import { SortableMenu } from 'app/views/boards/panels/ude/SortableMenu';
import { Position } from '@blueprintjs/core';
import DimensionMenu from 'app/views/boards/panels/ude/components/DimensionMenu';
import DropdownMenu from 'core/components/DropdownMenu';
import { getGroupedDimensions } from 'app/stores/ude/measurementTransforms';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';

// @ts-expect-error - fix typing for formConsumer
@formConsumer
@observer
class UdePanelDimensions extends Component<
  UdeFormProps & {
    measurements: Measurement[];
    measurementHash: MeasurementHash;
    appProtocols?: AppProtocolsDictionary;
    headerVariant?: 'default' | 'pane';
  },
  { isDropdownOpen: boolean }
> {
  state = {
    isDropdownOpen: false
  };

  handleRemoveDimension = (dimensionName: string) => {
    const { form } = this.props;
    const currentDimensions = form.getValue('config.data.dimensions') || [];
    const updatedDimensions = currentDimensions.filter((dimension) => dimension.name !== dimensionName);
    form.setValue('config.data.dimensions', updatedDimensions);
  };

  handleOpenAddDimension = () => {
    this.setState({ isDropdownOpen: true });
  };

  handleAddDimension = (newDim: string | { key: string }) => {
    const { form } = this.props;
    const currentDimensions = form.getValue('config.data.dimensions') || [];
    const newDimName = typeof newDim === 'string' ? newDim : newDim.key;

    const existingIndex = currentDimensions.findIndex((dimension) => dimension.name === newDimName);
    if (existingIndex >= 0) {
      // remove if already selected
      form.setValue(
        'config.data.dimensions',
        currentDimensions.filter((_, i) => i !== existingIndex)
      );
    } else {
      form.setValue('config.data.dimensions', [...currentDimensions, { name: newDimName }]);
    }
  };

  handleReorder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    const { form } = this.props;
    const currentDimensions = form.getValue('config.data.dimensions') || [];
    const updatedDimensions = Array.from(currentDimensions);
    const [movedDimension] = updatedDimensions.splice(oldIndex, 1);
    updatedDimensions.splice(newIndex, 0, movedDimension);
    form.setValue('config.data.dimensions', updatedDimensions);
  };

  render() {
    const { form, measurements, measurementHash, appProtocols, headerVariant } = this.props;
    const { isDropdownOpen } = this.state;
    const measurement = form.getValue('config.data.measurement');
    const metrics = form.getValue('config.data.metrics') || [];
    const dimensions = form.getValue('config.data.dimensions') || [];
    const hasMeasurement = !!measurement;

    const disabledDimensions = new Set(metrics.map((metric) => metric.name));

    const content = (
      <Flex flexDirection="column" px="12px" pt={0} pb={0} display="flex" gap={1}>
        {!dimensions?.length && (
          <Text muted small fontWeight="normal">
            No dimensions selected
          </Text>
        )}
        {dimensions?.length > 0 && (
          <SortableMenu
            measurement={form.getValue('config.data.measurement') || ''}
            type="dimension"
            handleRemoveItem={this.handleRemoveDimension}
            measurements={measurements}
            items={dimensions}
            onSortEnd={this.handleReorder}
          />
        )}
        {measurement?.startsWith('/metrics/') && (
          <Field name="config.data.reAgg.fn" mt={2} mb={0}>
            <Select />
          </Field>
        )}
      </Flex>
    );

    // TODO: we probably want to memoize this and pull it out into a single place
    const groupedDimensions = getGroupedDimensions(measurement ?? '', measurementHash)
      .map((group) => ({ ...group, items: group.items.filter((item) => !item.nodeData?.FilterOnly) }))
      .filter((group) => group.items.length > 0);

    const addButton = hasMeasurement ? (
      <Box ml="auto">
        <DropdownMenu
          isOpen={isDropdownOpen}
          onInteraction={(nextOpenState, e) => {
            if (nextOpenState) {
              this.setState({ isDropdownOpen: true });
              return;
            }
            // Suppress close events originating from inside the popover content (menu item clicks).
            // Only allow close from Escape or outside-click.
            const target = (e?.target as HTMLElement | null) ?? null;
            const isInsideContent = !!target?.closest('.ude-dimension-selector-popover');
            const isEscape = (e as unknown as KeyboardEvent | undefined)?.key === 'Escape';
            if (isInsideContent && !isEscape) {
              return;
            }
            this.setState({ isDropdownOpen: false });
          }}
          position={Position.RIGHT_TOP}
          popoverClassName="ude-dimension-selector-popover"
          boundary="viewport"
          modifiers={{
            flip: { enabled: true },
            hide: { enabled: false },
            preventOverflow: { enabled: true }
          }}
          usePortal
          canEscapeKeyClose
          content={
            <DimensionMenu
              measurement={measurement}
              measurementHash={measurementHash}
              groupedDimensions={groupedDimensions}
              selectedDimensions={dimensions}
              disabledDimensions={disabledDimensions}
              appProtocols={appProtocols}
              onSelect={this.handleAddDimension}
            />
          }
        >
          <Button
            small
            minimal
            intent="primary"
            text="Add"
            onClick={this.handleOpenAddDimension}
            active={isDropdownOpen}
          />
        </DropdownMenu>
      </Box>
    ) : null;

    return headerVariant === 'pane' ? (
      <>
        <Flex alignItems="center" gap={1} p="6px 8px 6px 16px">
          <Text small fontWeight="bold" style={{ textTransform: 'uppercase' as const }}>
            Dimensions
          </Text>
          {addButton}
        </Flex>
        {content}
      </>
    ) : (
      <Flex flexDirection="column" pb="12px">
        <Flex alignItems="center" gap={1} p="6px 8px 6px 16px">
          <Text small fontWeight="bold" style={{ textTransform: 'uppercase' as const }}>
            Dimensions
          </Text>
          {addButton}
        </Flex>
        {content}
      </Flex>
    );
  }
}

export default UdePanelDimensions;
